/**
 * Same-origin validation for a `?redirect=` target.
 *
 * The value is attacker-controllable via a login link, so an off-origin target
 * navigates the user away immediately after a genuine login on the genuine
 * domain — a ready-made phishing hand-off, and the more convincing for having
 * shown the real, correctly-branded login form first.
 *
 * WHY A PARSER, NOT STRING CHECKS: the first version of this rejected values
 * starting with `//` and `/\`, which looks sufficient and is not. The WHATWG URL
 * parser strips leading ASCII tab/LF/CR before parsing, so:
 *
 *   /%09/evil.com  ->  decodes to "/\t/evil.com"
 *                  ->  starts with "/" and not with "//"   (both checks pass)
 *                  ->  new URL(...) resolves to https://evil.com/
 *
 * Any hand-written character check is a guess about a parser's behaviour. Using
 * the parser itself removes the guess: resolve against a sentinel origin and
 * require the result to still be on it. Whatever normalization the browser would
 * apply, we applied first.
 */

/**
 * A base that cannot collide with a real origin. `.invalid` is reserved by
 * RFC 2606 precisely so it can never resolve.
 */
const SENTINEL_ORIGIN = "https://redirect.invalid";

/**
 * safeRedirect returns a same-origin path safe to hand to the router, or null
 * when the input is absent, malformed, or points off-origin.
 *
 * The returned value is the PARSED path, not the raw input, so control
 * characters and other oddities are normalized away rather than forwarded.
 */
export function safeRedirect(redirect: string | null | undefined): string | null {
  if (!redirect) return null;

  let url: URL;
  try {
    url = new URL(redirect, SENTINEL_ORIGIN);
  } catch {
    // Not parseable as a URL even relative to a base — nothing safe to do.
    return null;
  }

  // An off-origin target resolved to a different origin; a protocol-relative or
  // absolute URL lands here, as does anything the parser rewrote into one.
  if (url.origin !== SENTINEL_ORIGIN) return null;

  // A non-http(s) scheme (javascript:, data:) either fails to parse relative to
  // the base or changes the origin, but check explicitly rather than relying on
  // that being true for every scheme a browser might add.
  if (url.protocol !== "https:" && url.protocol !== "http:") return null;

  return `${url.pathname}${url.search}${url.hash}`;
}
